/** Represents the context where the current request is running. */
export interface Context {
  space: { key: string };
  userId: string | undefined;
}

export enum DatabaseChangeType {
  /** Add an entry to the database at the end or a specific index. */
  ADD_ENTRY = 'add_entry',

  /** Add a field to the database at the end or a specific index. */
  ADD_FIELD = 'add_field',

  /** Update the properties of a database field. */
  UPDATE_FIELD = 'update_field',

  /** Undo all UPDATE_FIELD, SET_FIELD_NAME, SET_FIELD_DESCRIPTION, SET_FIELD_TYPE, SET_FIELD_SETTINGS, and SET_AI_FIELD_CONFIG changes to a particular database field. Only makes sense in the context of addChangeToChangeset(). */
  RESET_FIELD = 'reset_field',

  /** Change one or multiple database field settings. */
  SET_FIELD_SETTINGS = 'set_field_settings',

  SET_AI_FIELD_CONFIG = 'set_ai_field_config',

  SET_FIELD_SETTINGS_NUMBER_FORMAT = 'set_field_settings_number_format',
  SET_FIELD_SETTINGS_MINIMUM_OR_MAXIMUM_FRACTION_DIGITS_FIELD = 'set_field_settings_minimum_or_maximum_fraction_digits_field',
  SET_FIELD_SETTINGS_SEPARATOR_FIELD = 'set_field_settings_separator_field',
  SET_FIELD_SETTINGS_THOUSANDS_AND_DECIMAL_FIELD = 'set_field_settings_thousands_and_decimal_field',
  SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD = 'set_field_settings_prefix_or_suffix_field',

  SET_IS_CREATABLE = 'set_is_creatable',
  SET_IS_MULTI = 'set_is_multi',
  SET_ALLOW_EDIT = 'set_allow_edit',
  SET_DISPLAY_DETAIL = 'set_display_detail',
  SET_CONTENT_FIELD = 'set_content_field',
  SET_BACKLINK_DISPLAY_TYPE = 'set_backlink_display_type',
  SET_DATABASE_ID = 'set_database_id',
  SET_INCLUDE_VERSION_COMMENT = 'set_include_version_comment',
  SET_JIRA_FIELD = 'set_jira_field',
  SET_JIRA_SERVER_ID = 'set_jira_server_id',
  SET_CLOUD_ID = 'set_cloud_id',
  SET_RESOLVE = 'set_resolve',
  SET_ENTRY_FIELD_FIELD = 'set_entry_field_field',
  SET_PAGE_SUB_TYPE = 'set_page_subtype',
  SET_PARENT_PAGE = 'set_parent_page',
  SET_PARENT_PAGE_OPTION = 'set_parent_page_option',
  SET_IS_PARENT_PAGE_SOURCE_DATABASE = 'set_is_parent_page_source_database',
  SET_PAGE_LOCATION = 'set_page_location',
  SET_PAGE_TEMPLATE = 'set_page_template',
  SET_DISPLAY_ENTRY_ON_PAGE = 'set_display_entry_on_page',
  SET_FIELD_ID = 'set_field_id',
  SET_SELECT_OPTIONS = 'set_select_options',
  SET_FIELD_SETTINGS_MEDIA_FORMAT = 'set_field_settings_media_format',

  /** Change the name of a database field. */
  SET_FIELD_NAME = 'set_field_name',

  /** Undo all SET_FIELD_NAME changes to a particular field name. Only makes sense in the context of addChangeToChangeset(). */
  RESET_FIELD_NAME = 'reset_field_name',

  /** Change the description of a database field. */
  SET_FIELD_DESCRIPTION = 'set_field_description',

  /** Show or hide the description of a database field. */
  SET_SHOW_FIELD_DESCRIPTION = 'set_show_field_description',

  /** Change the type of a database field. */
  SET_FIELD_TYPE = 'set_field_type',

  /** Set the width of a database field. */
  SET_FIELD_WIDTH = 'set_field_width',

  /** Move a field to the left or right. */
  REORDER_FIELD = 'reorder_field',

  /** Clone one or more fields including their settings and values. */
  CLONE_FIELDS = 'clone_fields',

  /** Delete a database field. */
  DELETE_FIELD = 'delete_field',

  /** Revert the deletion of a database field. Only makes sense in the context of addChangeToChangeset(). */
  UNDELETE_FIELD = 'undelete_field',

  /** Set a particular database value. */
  SET_VALUE = 'set_value',

  /** Creates a field and sets its value. */
  CREATE_FIELD_AND_SET_VALUE = 'create_field_and_set_value',

  /** Set a value for all entries that have no value for this field yet. */
  BULK_SET_UNDEFINED_VALUES = 'bulk_set_undefined_values',

  /** Undo all SET_VALUE changes to a particular database value. Only makes sense in the context of addChangeToChangeset(). */
  RESET_VALUE = 'reset_value',

  /** Move multiple entries up or down. */
  REORDER_ENTRIES = 'reorder_entries',

  /** Clone one or more entries including their values. */
  CLONE_ENTRIES = 'clone_entries',

  /** Delete a particular database entry. */
  DELETE_ENTRY = 'delete_entry',

  /** Revert the deletion of a particular database entry. Only makes sense in the context of addChangeToChangeset(). */
  UNDELETE_ENTRY = 'undelete_entry',

  ADD_SAVED_VIEW = 'add_saved_view',

  SET_SAVED_VIEW = 'set_saved_view',

  DELETE_SAVED_VIEW = 'delete_saved_view',

  REORDER_SAVED_VIEW = 'reorder_saved_view',

  SET_DEFAULT_SAVED_VIEW = 'set_default_saved_view',

  UPDATE_SAVED_VIEW_LAYOUT_TYPE = 'update_saved_view_layout_type',

  UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH = 'update_saved_view_layout_card_width',

  UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID = 'update_saved_view_layout_cover_field_id',

  UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES = 'update_saved_view_layout_show_field_names',

  UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID = 'update_saved_view_layout_group_field_id',

  SET_GROUPING_FIELD = 'set_grouping_field',

  SET_GROUPING_SORT_TYPE = 'set_grouping_sort_type',

  SET_GROUPING_MANUAL_GROUP_ORDER = 'set_grouping_manual_group_order',

  CLEAR_GROUPING = 'clear_grouping',

  UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID = 'update_saved_view_layout_date_field_id',

  UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES = 'update_saved_view_layout_show_numbered_entries',

  UPDATE_SAVED_VIEW_NAME = 'update_saved_view_name',

  UPDATE_SAVED_VIEW_FIELDS = 'update_saved_view_fields',

  UPDATE_SAVED_VIEW_SORT = 'update_saved_view_sort',

  UPDATE_SAVED_VIEW_SORT_VALUE = 'update_saved_view_sort_value',

  INSERT_SAVED_VIEW_SORT = 'insert_saved_view_sort',

  APPEND_SAVED_VIEW_SORT = 'append_saved_view_sort',

  DELETE_SAVED_VIEW_SORT = 'delete_saved_view_sort',

  REORDER_SAVED_VIEW_SORT = 'reorder_saved_view_sort',

  CLEAR_SAVED_VIEW_SORT = 'clear_saved_view_sort',

  SET_SAVED_VIEW_FILTER = 'set_saved_view_filter',

  SET_SAVED_VIEW_FILTER_USAGE_TYPE = 'set_saved_view_filter_usage_type',

  SET_SAVED_VIEW_FILTER_FIELD_ID = 'set_saved_view_filter_field_id',

  SET_SAVED_VIEW_FILTER_TYPE = 'set_saved_view_filter_type',

  SET_SAVED_VIEW_FILTER_VALUE = 'set_saved_view_filter_value',

  SET_SAVED_VIEW_CALCULATIONS = 'set_saved_view_calculations',

  REMOVE_SAVED_VIEW_CALCULATION = 'remove_saved_view_calculation',

  APPEND_SAVED_VIEW_FILTER = 'append_saved_view_filter',

  REMOVE_SAVED_VIEW_FILTER = 'remove_saved_view_filter',

  CLEAR_SAVED_VIEW_FILTER = 'clear_saved_view_filter',

  BULK_APPLY_SAVED_VIEW_CHANGES = 'bulk_apply_saved_view_changes',

  TOGGLE_LOCK_STRUCTURE = 'toggle_lock_structure',

  /** Removes the values for the given entryIds and optional given fieldIds */
  CLEAR_ENTRIES = 'clear_entries',

  SET_DEFAULT_ENABLED = 'set_default_enabled',

  SET_MENTION_NOTIFICATION_ENABLED = 'set_mention_notification_enabled',

  ADD_VALUE_COMMENT_THREAD = 'add_value_comment_thread',
  REMOVE_VALUE_COMMENT_THREAD = 'remove_value_comment_thread',
  SET_VALUE_COMMENT_THREAD = 'set_value_comment_thread',
  SET_VALUE_COMMENT_THREAD_RESOLVED = 'set_value_comment_thread_resolved',
  SET_VALUE_COMMENT_THREAD_COMMENT_COUNT = 'set_value_comment_thread_comment_count',
  INCREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT = 'increment_value_comment_thread_comment_count',
  DECREMENT_VALUE_COMMENT_THREAD_COMMENT_COUNT = 'decrement_value_comment_thread_comment_count',
  INCREMENT_VALUE_COMMENT_THREAD_VERSION_NUMBER = 'increment_value_comment_thread_version_number',

  SET_DATE_FIELD_DATE_FORMAT = 'set_date_format',
  SET_DATE_FIELD_TIME_FORMAT = 'set_time_format',
  SET_DATE_FIELD_ENABLE_DATE_RANGE = 'set_date_field_enable_date_range'
}
